"use client";

import { Home } from "@layer/icons";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from "@layer/ui/components";
import { useTabs } from "@layer/utils/hooks";
import { motion } from "motion/react";
import Link from "next/link";
import { Fragment } from "react";
import { useApplicationShell } from "../../application-shell-provider";

const MotionPage = motion.create(BreadcrumbPage);

const NavBreadcrumbs = ({ tabKey }: { tabKey: string }) => {
	const { segments, resolveBreadcrumbTitle: contextResolveBreadcrumbTitle } =
		useApplicationShell();
	const [tabs] = useTabs(tabKey);

	const pathArray =
		segments
			?.filter((path) => {
				// ignore route groups like (auth), (app), etc
				if (path.startsWith("(") && path.endsWith(")")) return false;
				return true;
			})
			.map((path, index) => ({
				title: path,
				href: `/${segments.slice(0, index + 1).join("/")}`,
			})) || [];

	const resolveBreadcrumbTitle = (title: string | undefined) => {
		if (!title) return title;

		const contextResolved = contextResolveBreadcrumbTitle?.(title);
		if (contextResolved) return contextResolved;

		if (tabs) {
			const match = tabs.find((tab) => tab.id === title);
			if (match) return match.name;
		}

		return title.replace(/\b\w/g, (c) => c.toUpperCase());
	};

	return (
		<Breadcrumb>
			<BreadcrumbList>
				{/* Home */}
				<BreadcrumbItem>
					<BreadcrumbLink asChild>
						<Link href="/" aria-label="Home">
							<Home className="h-3 w-3" variant="filled" />
						</Link>
					</BreadcrumbLink>
				</BreadcrumbItem>

				{/* Middle crumbs */}
				{pathArray
					.slice(0, Math.max(0, pathArray.length - 1))
					.map(({ href, title }) => (
						<Fragment key={href}>
							<BreadcrumbSeparator />
							<BreadcrumbItem>
								<BreadcrumbLink asChild>
									<Link href={href}>
										{resolveBreadcrumbTitle(title)}
									</Link>
								</BreadcrumbLink>
							</BreadcrumbItem>
						</Fragment>
					))}

				{/* Current page */}
				{pathArray.length > 0 && (
					<>
						<BreadcrumbSeparator />
						<BreadcrumbItem>
							<MotionPage
								key={pathArray[pathArray.length - 1]?.title}
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{
									type: "spring",
									stiffness: 500,
									damping: 30,
								}}
							>
								{resolveBreadcrumbTitle(
									pathArray[pathArray.length - 1]?.title,
								)}
							</MotionPage>
						</BreadcrumbItem>
					</>
				)}
			</BreadcrumbList>
		</Breadcrumb>
	);
};

export { NavBreadcrumbs };
